// turnkey-wallet-widget.ts - runs one Turnkey signer exchange inside the shared
// in-app WIDGET (bounded-widget-modal.ts). The Bounded-owned signer page is loaded
// in a warm iframe behind the modal so signing feels native: an in-app approve
// card (with inline code entry when the 24h signing session must first be
// established), never a jarring popup.
//
// WHY AN IFRAME: the wallet's signing session lives in the single Bounded signer
// origin's storage, out of every app's reach, so signing MUST run on that origin;
// the iframe brings it in-app.
//
// Restricted embedders: if the frame never loads, the SAME request reruns through
// `popupFallback` (a real window). Callers pass a popup implementation and never
// see the difference.
import { getModal, showModal, hideModal, ensureIframe, setPanelHeight, hasDOM, } from "./bounded-widget-modal";
/**
 * Run one signer exchange through the in-app widget. Resolves when `onResult`
 * accepts a result; falls back to `popupFallback` if the frame cannot be mounted
 * or never loads.
 */
export async function runWidget(url, signerOrigin, ex, popupFallback) {
    if (!hasDOM())
        return popupFallback();
    const m = getModal();
    const requestId = String(ex.request.requestId);
    // Serialize: the approve card is modal, and one warm frame handles one request at a time.
    const run = m.chain.then(() => new Promise((resolve, reject) => {
        let settled = false;
        let usedFallback = false;
        const iframe = ensureIframe(m, url, signerOrigin);
        const send = () => {
            if (!iframe.contentWindow)
                return;
            try {
                iframe.contentWindow.postMessage(ex.request, signerOrigin);
            }
            catch (_a) {
                /* frame gone */
            }
        };
        let loadTimer = 0;
        let ceilingTimer = 0;
        const finish = (fn) => {
            if (settled)
                return;
            settled = true;
            m.onIframeMessage = null;
            m.onBackdrop = null;
            window.clearTimeout(loadTimer);
            window.clearTimeout(ceilingTimer);
            fn();
        };
        const goFallback = () => {
            if (usedFallback || settled)
                return;
            usedFallback = true;
            hideModal(m);
            finish(() => popupFallback().then(resolve, reject));
        };
        m.onBackdrop = () => {
            hideModal(m);
            finish(() => reject(new Error("cancelled")));
        };
        m.onIframeMessage = (d) => {
            if (d.type === "bounded:turnkey:ready") {
                m.iframeReady = true;
                send();
                return;
            }
            if (d.type === "bounded:turnkey:resize" && typeof d.height === "number") {
                setPanelHeight(m, d.height);
                return;
            }
            if (d.type === "bounded:turnkey:result" && d.requestId === requestId) {
                const outcome = ex.onResult(d);
                if ("error" in outcome) {
                    hideModal(m);
                    finish(() => reject(new Error(outcome.error)));
                    return;
                }
                if (outcome.done) {
                    hideModal(m);
                    finish(() => resolve(outcome.value));
                }
            }
        };
        showModal(m);
        if (m.iframeReady)
            send();
        // If the frame never loads, degrade to the popup (best-effort - a post-load
        // popup may be blocked without a gesture, in which case the fallback rejects).
        loadTimer = window.setTimeout(() => {
            if (!m.iframeReady && !settled)
                goFallback();
        }, 8000);
        // Absolute ceiling so a stuck flow cannot hang forever.
        ceilingTimer = window.setTimeout(() => {
            if (!settled) {
                hideModal(m);
                finish(() => reject(new Error("Wallet approval timed out.")));
            }
        }, 5 * 60 * 1000);
    }));
    m.chain = run.then(() => undefined, () => undefined);
    return run;
}
